import PropTypes from 'prop-types';
import {
	BsCheckCircleFill,
	BsClockFill,
	BsCreditCardFill,
	BsEnvelopeFill,
	BsInboxFill,
	BsPrinterFill,
	BsSendFill,
	BsXCircleFill,
} from 'react-icons/bs';

import { formatDate } from '#lib/dateUtils';

const STEP_KEYS = {
	PAY: 'pay',
	PAID: 'paid',
	PRINTER: 'printer',
	PRINTED: 'printed',
	POSTED: 'posted',
	DELIVERY: 'delivery',
};

// Index into the rendered step list. Payment states stop short of the production chain; everything
// from 'sending' onwards is driven by the per-letter milestones eBrief reports, not by the status.
const STEP_INDEX = {
	[STEP_KEYS.PAY]: 0,
	[STEP_KEYS.PAID]: 1,
	[STEP_KEYS.PRINTER]: 2,
	[STEP_KEYS.PRINTED]: 3,
	[STEP_KEYS.POSTED]: 4,
	[STEP_KEYS.DELIVERY]: 5,
};

const TERMINAL_STATUSES = new Set(['failed', 'cancelled']);

const CIRCLE_STYLES = {
	done: 'bg-success-200 text-success-700',
	active: 'bg-primary-200 text-primary-600',
	future: 'bg-bgBase-400 text-bgBase-800',
	failed: 'bg-warning-200 text-warning-700',
	cancelled: 'bg-bgBase-500 text-bgBase-800',
};

/**
 * Earliest timestamp recorded for a milestone across the campaign's letters, plus how many reached it.
 * The batch moves through production in waves, so the first letter to reach a stage is what the
 * customer experiences as "this stage started".
 *
 * @param {object[]} sends
 * @param {string} field - Milestone column name on a send
 * @returns {{at: string|null, count: number}}
 */
function milestone(sends, field) {
	const stamps = sends.map((send) => send[field]).filter(Boolean);
	if (!stamps.length) return { at: null, count: 0 };
	return { at: stamps.reduce((earliest, cur) => (cur < earliest ? cur : earliest)), count: stamps.length };
}

/**
 * Map campaign status + per-letter milestones to the step the campaign has reached.
 *
 * @param {string} status
 * @param {object} reached - { printer, printed, posted } milestone summaries
 * @returns {number}
 */
function currentStep(status, reached) {
	if (status === 'awaiting_payment') return STEP_INDEX.pay;
	if (status === 'payment_processing') return STEP_INDEX.paid;
	if (reached.posted.count) return STEP_INDEX.delivery;
	if (reached.printed.count) return STEP_INDEX.printed;
	if (reached.printer.count) return STEP_INDEX.printer;
	return STEP_INDEX.printer;
}

/**
 * The explanatory line under the rail. This is where the customer learns that "versendet" does not
 * mean "delivered" — the single most important piece of copy in the timeline.
 *
 * @param {object} params
 * @returns {{text: string, tone: string}|null}
 */
function statusNote({ status, reached, failedCount, total }) {
	if (status === 'awaiting_payment')
		return { text: 'Warten auf den Abschluss der Zahlung.', tone: 'text-bgBase-800' };
	if (status === 'payment_processing') {
		return {
			text: 'Ihre SEPA-Lastschrift wird verarbeitet. Sobald die Zahlung eingegangen ist (in der Regel 3–6 Werktage), werden die Briefe automatisch versendet.',
			tone: 'text-primary-700',
		};
	}
	if (status === 'awaiting_offer') {
		return {
			text: 'Ihr Sonderformat wird geprüft. Sie erhalten ein individuelles Angebot per E-Mail.',
			tone: 'text-bgBase-800',
		};
	}
	if (status === 'cancelled') return { text: 'Diese Kampagne wurde storniert.', tone: 'text-bgBase-800' };
	if (status === 'failed') {
		return {
			text: 'Der Versand ist fehlgeschlagen. Bereits berechnete, nicht versendete Briefe werden erstattet.',
			tone: 'text-warning-700',
		};
	}
	if (status === 'needs_review') {
		return {
			text: 'Einige Anschriften müssen geprüft werden. Der Versand wird fortgesetzt, sobald Sie diese freigegeben oder übersprungen haben.',
			tone: 'text-caution-700',
		};
	}

	if (reached.posted.at) {
		const problems = failedCount ? ` ${failedCount} von ${total} Briefen konnten nicht zugestellt werden.` : '';
		return {
			text: `Am ${formatDate(reached.posted.at)} an die Deutsche Post übergeben. ${problems}`,
			tone: failedCount ? 'text-caution-700' : 'text-text',
		};
	}
	if (reached.printed.at) {
		return {
			text: `Gedruckt und kuvertiert am ${formatDate(reached.printed.at)}. Die Übergabe an die Deutsche Post erfolgt in der Regel am selben oder nächsten Werktag.`,
			tone: 'text-text',
		};
	}
	if (reached.printer.at) {
		return {
			text: `Ihre Briefe wurden am ${formatDate(reached.printer.at)} an unseren Druckdienstleister übergeben. Druck und Kuvertierung dauern in der Regel 1–2 Werktage.`,
			tone: 'text-text',
		};
	}
	return { text: 'Ihre Briefe werden für den Druck vorbereitet.', tone: 'text-bgBase-800' };
}

/**
 * @param {object} props
 * @returns {JSX.Element}
 */
function TimelineStep({ step, variant, isLast, connectorDone }) {
	let Icon = step.icon;
	if (variant === 'done') Icon = BsCheckCircleFill;
	// A stopped campaign gets a cross rather than its stage icon, so the rail reads as ended, not paused.
	if (variant === 'failed' || variant === 'cancelled') Icon = BsXCircleFill;
	const isMuted = variant === 'future';
	return (
		<div className="flex flex-1 items-center">
			<div className="flex flex-col items-center">
				<div className={`flex h-9 w-9 items-center justify-center rounded-full ${CIRCLE_STYLES[variant]}`}>
					<Icon className={`h-5 w-5 ${variant === 'active' ? 'animate-pulse' : ''}`} />
				</div>
				<span className={`mt-1.5 text-center text-xs ${isMuted ? 'text-bgBase-800' : 'font-medium text-text'}`}>
					{step.label}
				</span>
				{step.at && <span className="text-center text-[11px] text-bgBase-800">{formatDate(step.at)}</span>}
				{step.hint && <span className="text-center text-[11px] text-bgBase-800">{step.hint}</span>}
			</div>
			{!isLast && <div className={`mx-2 h-0.5 flex-1 ${connectorDone ? 'bg-success-500' : 'bg-bgBase-500'}`} />}
		</div>
	);
}

TimelineStep.propTypes = {
	step: PropTypes.shape({
		label: PropTypes.string.isRequired,
		icon: PropTypes.elementType.isRequired,
		at: PropTypes.string,
		hint: PropTypes.string,
	}).isRequired,
	variant: PropTypes.oneOf(['done', 'active', 'future', 'failed', 'cancelled']).isRequired,
	isLast: PropTypes.bool.isRequired,
	connectorDone: PropTypes.bool.isRequired,
};

/**
 * Delivery timeline for a post-mailing campaign: payment through production to the expected delivery
 * date. Unlike a plain status badge it distinguishes "at the printer" from "handed to Deutsche Post",
 * because eBrief reports those as separate milestones days apart.
 *
 * @param {object} props
 * @returns {JSX.Element|null}
 */
export default function PostMailingStatusTimeline({ status, sends }) {
	if (status === 'draft') return null;

	const letters = sends ?? [];
	const reached = {
		printer: milestone(letters, 'atPrinterAt'),
		printed: milestone(letters, 'printedAt'),
		posted: milestone(letters, 'sentAt'),
	};

	const total = letters.length;
	const failedCount = letters.filter((send) => ['failed', 'returned', 'cancelled'].includes(send.status)).length;
	const isTerminal = TERMINAL_STATUSES.has(status);
	const current = currentStep(status, reached);

	// A partially-progressed stage is worth calling out: "3 von 50" explains why the campaign looks
	// finished while letters are still moving.
	const partial = (m) => (m.count && total && m.count < total ? `${m.count} von ${total}` : undefined);

	const steps = [
		{ key: STEP_KEYS.PAY, label: 'Zahlung', icon: BsCreditCardFill },
		{
			key: STEP_KEYS.PAID,
			label: 'Bezahlt',
			icon: status === 'payment_processing' ? BsClockFill : BsCheckCircleFill,
		},
		{
			key: STEP_KEYS.PRINTER,
			label: 'Im Druck',
			icon: BsPrinterFill,
			at: reached.printer.at,
			hint: partial(reached.printer),
		},
		{
			key: STEP_KEYS.PRINTED,
			label: 'Gedruckt & kuvertiert',
			icon: BsEnvelopeFill,
			at: reached.printed.at,
			hint: partial(reached.printed),
		},
		{
			key: STEP_KEYS.POSTED,
			label: 'Bei der Post',
			icon: BsSendFill,
			at: reached.posted.at,
			hint: partial(reached.posted),
		},
		// Dropped on a terminal campaign: there is no delivery left to expect.
		...(isTerminal
			? []
			: [{ key: STEP_KEYS.DELIVERY, label: 'Zustellung erwartet', icon: BsInboxFill, hint: 'Schätzung' }]),
	];

	const note = statusNote({ status, reached, failedCount, total });

	return (
		<div className="mb-6 rounded-card border border-bgBase-500 bg-white p-4">
			<div className="flex items-start justify-between">
				{steps.map((step, index) => {
					const isLast = index === steps.length - 1;
					let variant = 'future';
					if (isTerminal && index === current) variant = status === 'failed' ? 'failed' : 'cancelled';
					else if (index < current) variant = 'done';
					else if (index === current) variant = 'active';

					return (
						<TimelineStep
							key={step.key}
							step={step}
							variant={variant}
							isLast={isLast}
							connectorDone={index < current}
						/>
					);
				})}
			</div>

			{note && <p className={`mt-3 text-xs ${note.tone}`}>{note.text}</p>}
			{!isTerminal && reached.posted.at && (
				<p className="mt-1 text-xs text-bgBase-800">Die Zustellung dauert in der Regel 2–4 Werktage.</p>
			)}
		</div>
	);
}

PostMailingStatusTimeline.propTypes = {
	status: PropTypes.string.isRequired,
	sends: PropTypes.arrayOf(
		PropTypes.shape({
			status: PropTypes.string,
			atPrinterAt: PropTypes.string,
			printedAt: PropTypes.string,
			sentAt: PropTypes.string,
		}),
	),
};
